import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { showNotification } from '@mantine/notifications';
import set from 'lodash.set';
import merge from 'lodash.merge';

import { Routes } from '~/domains/common/constants/routes';
import { usePreventLocationChange } from '~/domains/common/hooks/usePreventLocationChange';
import { BRANDS } from '~/domains/auth/consts';
import useRedirectToEO from '~/domains/common/utils/useRedirectToEO';
import type { ContestTemplate } from '~/services/contests/interfaces/contest-templates.interface';

import { ContestManageContext } from '../context/ContestManageContext';
import type { FormValues } from '../types/FormValues';
import { replaceImageFilesWithAvatarURL } from '../context/utils/replaceFilesImages';
import flattenErrors from '../context/utils/flattenErrors';
import type { FormattedErrors } from '../context/utils/formatZodErrors';
import { useSectionsNavigation } from '../context/hooks/useSectionsNavigation';
import { reportManageContestError, reportManageContestSuccess } from '../analytics';
import { postManageContest } from '../../../server/service';
import { manageSchema } from '../api/new/post/post.schema';

import { useAnonymousContestCreation } from './useAnonymousContestCreation';

function setTouchedFromErrors(errorsObject: FormattedErrors) {
  const touched = {};

  function collectPaths(obj, path = []) {
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      Object.keys(obj).forEach((key) => {
        collectPaths(obj[key], path.concat(key));
      });
    } else {
      set(touched, path, true);
    }
  }

  collectPaths(errorsObject);
  return touched;
}

type UseSubmitProps = {
  leagueId: string;
  contestTemplateId: string;
  contestId: string;
  brand: string;
  eoNeedsRestart?: boolean;
  operation: 'create' | 'edit' | 'restart';
  entertainmentOnly: ContestTemplate['settings']['entertainmentOnly'];
};

const AUTO_CLOSE_TIME = 5_000;

const useSubmit = ({
  eoNeedsRestart,
  leagueId,
  contestTemplateId,
  contestId,
  brand,
  operation,
  entertainmentOnly,
}: UseSubmitProps) => {
  const { values, errors, setTouched } = useContext(ContestManageContext);
  const { currentSection, isNextSection } = useSectionsNavigation();
  const [discardChangesModalPayload, setDiscardChangesModalPayload] = useState(null);
  const router = useRouter();
  const retries = useRef(0);
  const contestUserId = router.query.contestUserId as string;
  const { redirectToEO, isFetching } = useRedirectToEO();

  const currentQuery = router.query;
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const {
    isAnonymous,
    setValue: setAnonValue,
    value: anonValues,
    clearValue: clearAnonValue,
    hasValue: hasAnonValue,
    isAnonURL,
  } = useAnonymousContestCreation<FormValues>();

  const submitMutation = useMutation({
    mutationFn: async (formValues: FormValues) => {
      const updatedValues = await replaceImageFilesWithAvatarURL(formValues);

      return postManageContest({
        contestTemplateId,
        leagueId,
        brand,
        contestId,
        operation,
        ...updatedValues,
      });
    },
    onSuccess: (data) => {
      reportManageContestSuccess(operation, data);
      window.scrollTo({ top: 0 });
    },
    onError: (error: AxiosError<{ message?: string | string[] }>) => {
      const errorMessage = error?.response?.data?.message;

      reportManageContestError(
        operation,
        Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage
      );

      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((message) => {
          showNotification({
            message,
            color: 'orange.3',
            autoClose: AUTO_CLOSE_TIME,
          });
        });
      } else {
        showNotification({
          message: errorMessage,
          color: 'orange.3',
          autoClose: AUTO_CLOSE_TIME,
        });
      }
    },
    onSettled: (data) => {
      if (!data) return;
      clearAnonValue();
      const refPlatform = router.query?.refPlatform;

      if (refPlatform === 'android' || refPlatform === 'ios') {
        window.location.href = Routes.appSplashContestDetailRedirect(data.id);
      }
    },
  });

  // Strip ?anon from URL after successful anonymous comeback submission
  useEffect(() => {
    if (submitMutation.isSuccess && isAnonURL) {
      const { anon, ...restQuery } = router.query;
      router.push({ pathname: router.pathname, query: restQuery });
    }
  }, [submitMutation.isSuccess, isAnonURL, router]);

  const isSelfManagedSelected =
    values?.form?.entriesSettings?.entryFeeSettings?.settings?.mode === 'self';
  const contestTitle = values?.basicInfo?.contestName;

  const handleAnonymousSubmit = useCallback(
    async (formValues: FormValues) => {
      if (errors) {
        const notifications = flattenErrors(errors);
        notifications.forEach((notification) => {
          showNotification({
            message: notification,
            color: 'orange.3',
            autoClose: AUTO_CLOSE_TIME,
          });
        });
        const newTouched = setTouchedFromErrors(errors);
        setTouched((prevTouched) => merge(prevTouched, newTouched));
        return;
      }

      const updatedValues = await replaceImageFilesWithAvatarURL(formValues);

      // Validate against the server POST schema before redirecting to login
      const payload = {
        contestTemplateId,
        leagueId,
        brand,
        contestId,
        operation,
        ...updatedValues,
      };
      const result = manageSchema.safeParse(payload);
      if (result.success === false) {
        result.error.issues.forEach((issue) => {
          showNotification({
            message: issue.message,
            color: 'orange.3',
            autoClose: AUTO_CLOSE_TIME,
          });
        });
        return;
      }

      setAnonValue(updatedValues);

      const redirectParams = new URLSearchParams({ anon: 'true' });
      if (leagueId) redirectParams.set('leagueId', leagueId);
      if (contestTemplateId) redirectParams.set('contestTemplateId', contestTemplateId);
      router.replace(Routes.signUp({ redirectTo: `contest/create?${redirectParams.toString()}` }));
    },
    [
      errors,
      router,
      setAnonValue,
      setTouched,
      leagueId,
      contestTemplateId,
      brand,
      contestId,
      operation,
    ]
  );

  const handleSubmit = useCallback(() => {
    setIsSubmitClicked(true);

    if (hasAnonValue && isAnonURL) {
      submitMutation.mutate(anonValues);
      return;
    }

    if (eoNeedsRestart && isSelfManagedSelected) {
      if (brand === BRANDS.RYP) {
        redirectToEO(Routes.restartRYP(contestUserId));
      } else if (brand === BRANDS.OFP) {
        redirectToEO(Routes.restartOFP(contestUserId));
      }
      return;
    }

    if (entertainmentOnly?.url && isSelfManagedSelected) {
      const path = `${entertainmentOnly?.url}&contestTitle=${contestTitle}`;
      if (entertainmentOnly?.brand === BRANDS.RYP) {
        redirectToEO(Routes.redirectToEoRyp(path));
      } else if (entertainmentOnly?.brand === BRANDS.OFP) {
        redirectToEO(Routes.redirectToEoOfp(path));
      }
      return;
    }

    if (errors) {
      const notifications = flattenErrors(errors);
      notifications.forEach((notification) => {
        showNotification({
          message: notification,
          color: 'orange.3',
          autoClose: AUTO_CLOSE_TIME,
        });
      });
      const newTouched = setTouchedFromErrors(errors);
      setTouched((prevTouched) => merge(prevTouched, newTouched));
      return;
    }

    submitMutation.mutateAsync(values).catch((error) => {
      if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
        if (retries.current < 2) {
          retries.current += 1;

          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(submitMutation.mutate(values));
            }, 1000);
          });
        }
        retries.current = 0;
      }
    });
  }, [
    errors,
    submitMutation,
    values,
    setTouched,
    isSelfManagedSelected,
    eoNeedsRestart,
    contestUserId,
    brand,
    redirectToEO,
    entertainmentOnly,
    contestTitle,
    hasAnonValue,
    isAnonURL,
    anonValues,
    currentQuery,
    router,
  ]);

  usePreventLocationChange(
    useCallback(
      ({ next }) =>
        new Promise((resolve) => {
          const shouldPreventNavigate = (() => {
            if (Object.keys(values ?? {}).length === 0) return false;
            if (submitMutation.isSuccess || isAnonymous) return false;
            if (currentQuery) return false;

            return true;
          })();

          if (!shouldPreventNavigate) {
            resolve(next());
            return;
          }

          setDiscardChangesModalPayload(() => () => {
            resolve(next());
          });
        }),
      [submitMutation.isSuccess, values, isAnonymous, currentQuery]
    )
  );

  return {
    isSubmitClicked,
    onSubmit: handleSubmit,
    onAnonymousSubmit: handleAnonymousSubmit,
    isAnonymous,
    hasAnonValue,
    isAnonURL,
    isSubmitVisible: !isNextSection && !currentSection?.error,
    isSubmitSuccess: submitMutation.isSuccess,
    isSubmitLoading: submitMutation.isLoading || isFetching,
    submitError: submitMutation.error,
    data: submitMutation.data,
    isDiscardChangesModalOpen: !!discardChangesModalPayload,
    onDiscardChangesModalClose: () => setDiscardChangesModalPayload(null),
    onDiscardChangesModalConfirm: () => discardChangesModalPayload(),
  };
};

export default useSubmit;
